import React, { useState, useMemo, useEffect, useRef } from "react";
import { Plus } from "lucide-react";
import { PortalDsButton } from "src/components/portal-design-system/portal-ds-button";
import { PortalDsField, PortalDsSelect } from "src/components/portal-design-system/portal-ds-field";
import ExternalRepsTable from "../components/external-reps-table";
import ExternalRepDrawer from "../components/external-rep-drawer";
import type { ExternalRep } from "../types/commissions.types";
import {
  useGetCommissionInvoicesQuery,
  useGetExternalRepsQuery,
  useGetCommissionReportsQuery,
  useCreateExternalRepMutation,
  useUpdateExternalRepMutation,
} from "../endpoints/commissions.endpoints";
import { useGetCompaniesQuery } from "src/redux/billing/billing.endpoints";
import {
  monthYearToQuarterPeriod,
  useReportCommissionViewPeriod,
} from "../commission-view-period-context";

const PERIOD_TYPE_OPTIONS = [
  { value: "month", label: "Month" },
  { value: "quarter", label: "Quarter" },
];

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => {
  const month = i + 1;
  const label = new Date(2000, i, 1).toLocaleString("default", { month: "long" });
  return { value: String(month), label };
});

function getQuartersForPeriod(): { label: string; year: number; quarter: number }[] {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const currentQuarter = Math.ceil(currentMonth / 3);
  const quarters: { label: string; year: number; quarter: number }[] = [];
  for (let i = -2; i <= 1; i++) {
    const total = currentYear * 4 + currentQuarter + i;
    const year = Math.floor((total - 1) / 4);
    const quarter = ((total - 1) % 4) + 1;
    quarters.push({ label: `Q${quarter} ${year}`, year, quarter });
  }
  return quarters;
}

function getYearsForPeriod(): number[] {
  const y = new Date().getFullYear();
  return [y - 1, y, y + 1];
}

const QUARTERS = getQuartersForPeriod();
const YEARS = getYearsForPeriod();

const ExternalRepsView: React.FC = () => {
  const { data: commissionInvoices = [] } = useGetCommissionInvoicesQuery();
  const { data: repsData = [] } = useGetExternalRepsQuery();
  const { data: companies = [] } = useGetCompaniesQuery();
  const [createRep] = useCreateExternalRepMutation();
  const [updateRep] = useUpdateExternalRepMutation();
  const invoiceOptions = useMemo(
    () =>
      commissionInvoices.map((inv) => ({
        value: inv.id,
        label: `${inv.invoiceNumber} – ${inv.companyName}`,
      })),
    [commissionInvoices]
  );
  const companyOptions = useMemo(
    () =>
      companies.map((c) => ({
        value: c.ext_customer_id ?? c.ext_company_id ?? c.id,
        label: c.company_name ?? "",
      })),
    [companies]
  );
  const companyIdToName = useMemo(
    () =>
      companies.reduce<Record<string, string>>(
        (acc, c) => {
          const name = c.company_name ?? "";
          acc[c.id] = name;
          if (c.ext_customer_id) acc[c.ext_customer_id] = name;
          if (c.ext_company_id) acc[c.ext_company_id] = name;
          return acc;
        },
        {}
      ),
    [companies]
  );
  const companyIdToExtCustomerId = useMemo(
    () =>
      companies.reduce<Record<string, string>>(
        (acc, c) => {
          const ext = c.ext_customer_id ?? c.ext_company_id ?? c.id;
          acc[c.id] = ext;
          if (c.ext_customer_id) acc[c.ext_customer_id] = ext;
          if (c.ext_company_id) acc[c.ext_company_id] = ext;
          return acc;
        },
        {}
      ),
    [companies]
  );

  const [reps, setReps] = useState<ExternalRep[]>([]);
  const prevRepsDataRef = useRef(repsData);

  useEffect(() => {
    if (prevRepsDataRef.current !== repsData) {
      prevRepsDataRef.current = repsData;
      setReps(repsData);
    } else if (repsData.length > 0 && reps.length === 0) {
      setReps(repsData);
    }
  }, [repsData, reps.length]);

  const [periodType, setPeriodType] = useState<"month" | "quarter">("quarter");
  const [selectedMonth, setSelectedMonth] = useState(() => String(new Date().getMonth() + 1));
  const [selectedYear, setSelectedYear] = useState(() => String(new Date().getFullYear()));
  const [selectedQuarter, setSelectedQuarter] = useState(() => {
    const now = new Date();
    const y = now.getFullYear();
    const q = Math.ceil((now.getMonth() + 1) / 3);
    return `Q${q} ${y}`;
  });
  const [drawerOpen, setDrawerOpen] = useState(false);

  const periodLabel = useMemo(() => {
    if (periodType === "month") {
      const monthIndex = Number(selectedMonth) - 1;
      const monthName = new Date(2000, monthIndex, 1).toLocaleString("default", {
        month: "long",
      });
      return `${monthName} ${selectedYear}`;
    }
    return selectedQuarter;
  }, [periodType, selectedMonth, selectedYear, selectedQuarter]);

  const viewPeriodForLoad = useMemo(() => {
    if (periodType === "quarter") {
      const q = QUARTERS.find((x) => x.label === selectedQuarter);
      if (!q) return null;
      return { quarter: `Q${q.quarter}`, year: q.year };
    }
    const month = Number(selectedMonth);
    const year = Number(selectedYear);
    if (!month || !year) return null;
    return monthYearToQuarterPeriod(month, year);
  }, [periodType, selectedMonth, selectedYear, selectedQuarter]);

  useReportCommissionViewPeriod(viewPeriodForLoad);

  const reportParamsList = useMemo(() => {
    if (periodType === "month") {
      const month = Number(selectedMonth);
      const year = Number(selectedYear);
      const p = { type: "external_rep" as const, month, year };
      return [p];
    }
    const q = QUARTERS.find((x) => x.label === selectedQuarter);
    if (!q) return [];
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    const quarterMonths = [
      { type: "external_rep" as const, month: (q.quarter - 1) * 3 + 1, year: q.year },
      { type: "external_rep" as const, month: (q.quarter - 1) * 3 + 2, year: q.year },
      { type: "external_rep" as const, month: (q.quarter - 1) * 3 + 3, year: q.year },
    ];
    const onlyCompletedMonths = quarterMonths.filter(
      (p) => p.year < currentYear || (p.year === currentYear && p.month < currentMonth)
    );
    if (onlyCompletedMonths.length === 0) return [];
    return onlyCompletedMonths;
  }, [periodType, selectedMonth, selectedYear, selectedQuarter]);

  const reportParams0 = reportParamsList[0];
  const reportParams1 = reportParamsList[1];
  const reportParams2 = reportParamsList[2];
  const skipReports = reportParamsList.length === 0;
  const { data: reports1 = [] } = useGetCommissionReportsQuery(reportParams0 ?? { type: "external_rep" }, { skip: skipReports });
  const { data: reports2 = [] } = useGetCommissionReportsQuery(reportParams1 ?? reportParams0 ?? { type: "external_rep" }, { skip: skipReports || reportParamsList.length < 2 });
  const { data: reports3 = [] } = useGetCommissionReportsQuery(reportParams2 ?? reportParams0 ?? { type: "external_rep" }, { skip: skipReports || reportParamsList.length < 3 });

  const repIdToEstimatedCommission = useMemo(() => {
    const sources = [reports1, reports2, reports3].slice(0, reportParamsList.length);
    const all = sources.flat();
    const byRep: Record<string, number> = {};
    for (const r of all) {
      const id = r.externalRepId ?? (r.details as { external_rep_id?: string } | undefined)?.external_rep_id ?? "";
      if (!id) continue;
      byRep[id] = (byRep[id] ?? 0) + (r.commission ?? 0);
    }
    return byRep;
  }, [reports1, reports2, reports3, reportParamsList.length]);

  const [selectedRep, setSelectedRep] = useState<ExternalRep | null>(null);
  const [isNewRep, setIsNewRep] = useState(false);

  useEffect(() => {
    if (!drawerOpen || !selectedRep?.id || !repsData.length) return;
    const updated = repsData.find((r) => r.id === selectedRep.id);
    if (updated && updated !== selectedRep) setSelectedRep(updated);
  }, [repsData, drawerOpen, selectedRep?.id]);

  const handleRowClick = (rep: ExternalRep) => {
    setSelectedRep(rep);
    setIsNewRep(false);
    setDrawerOpen(true);
  };

  const handleAddRep = () => {
    setSelectedRep(null);
    setIsNewRep(true);
    setDrawerOpen(true);
  };

  const handleSaveRep = async (payload: Partial<ExternalRep> & { id?: string }) => {
    const name = payload.organizationName ?? payload.name ?? "";
    const email = payload.contactEmail ?? payload.email ?? "";
    const baseRatePercent = payload.recurringRatePercent ?? payload.initialRatePercent ?? 0;
    const initialBonusRatePercent = Math.max(
      0,
      (payload.initialRatePercent ?? 0) - (payload.recurringRatePercent ?? 0)
    );

    if (isNewRep) {
      try {
        await createRep({
          name,
          email,
          planType: payload.planType ?? "flat_percent",
          baseRatePercent,
          initialBonusRatePercent,
        }).unwrap();
        setDrawerOpen(false);
      } catch {
        // Error could be shown via toast
      }
    } else if (payload.id) {
      try {
        await updateRep({
          id: payload.id,
          name,
          email,
          planType: payload.planType,
          baseRatePercent,
          initialBonusRatePercent,
        }).unwrap();
        setReps((prev) =>
          prev.map((r) =>
            r.id === payload.id
              ? {
                  ...r,
                  name,
                  email,
                  planType: payload.planType ?? r.planType,
                  organizationName: name,
                  contactEmail: email,
                  assignedCustomerIds: payload.assignedCustomerIds ?? r.assignedCustomerIds,
                  initialRatePercent: payload.initialRatePercent ?? r.initialRatePercent,
                  recurringRatePercent: payload.recurringRatePercent ?? r.recurringRatePercent,
                }
              : r
          )
        );
      } catch {
        // Error could be shown via toast
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <PortalDsField
            label="View by"
            htmlFor="external-period-type"
            filter
            className="w-[8.5rem]"
          >
            <PortalDsSelect
              id="external-period-type"
              value={periodType}
              onChange={(e) => setPeriodType(e.target.value as "month" | "quarter")}
            >
              {PERIOD_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </PortalDsSelect>
          </PortalDsField>
          {periodType === "month" ? (
            <>
              <PortalDsField
                label="Month"
                htmlFor="external-month"
                filter
                className="w-[9.5rem]"
              >
                <PortalDsSelect
                  id="external-month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                >
                  {MONTH_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </PortalDsSelect>
              </PortalDsField>
              <PortalDsField
                label="Year"
                htmlFor="external-year"
                filter
                className="w-[7rem]"
              >
                <PortalDsSelect
                  id="external-year"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                >
                  {YEARS.map((y) => (
                    <option key={y} value={String(y)}>
                      {y}
                    </option>
                  ))}
                </PortalDsSelect>
              </PortalDsField>
            </>
          ) : (
            <PortalDsField
              label="Quarter"
              htmlFor="external-quarter"
              filter
              className="w-[8.5rem]"
            >
              <PortalDsSelect
                id="external-quarter"
                value={selectedQuarter}
                onChange={(e) => setSelectedQuarter(e.target.value)}
              >
                {QUARTERS.map((q) => (
                  <option key={q.label} value={q.label}>
                    {q.label}
                  </option>
                ))}
              </PortalDsSelect>
            </PortalDsField>
          )}
        </div>
        <PortalDsButton type="button" variant="secondary" size="sm" onClick={handleAddRep}>
          <Plus className="h-4 w-4" />
          Add rep
        </PortalDsButton>
      </div>
      <ExternalRepsTable
        reps={reps}
        quarterLabel={periodLabel}
        onRowClick={handleRowClick}
        companyIdToName={companyIdToName}
        repIdToEstimatedCommission={repIdToEstimatedCommission}
      />
      <ExternalRepDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        rep={selectedRep}
        isNew={isNewRep}
        onSave={handleSaveRep}
        invoiceOptions={invoiceOptions}
        companyOptions={companyOptions}
        companyIdToExtCustomerId={companyIdToExtCustomerId}
        commissionInvoices={commissionInvoices}
      />
    </div>
  );
};

export default ExternalRepsView;
